import { SiteWiseClient } from './client/client';
import { toId } from './util/dataStreamId';
import { viewportEndDate, viewportStartDate, parseDuration, } from '@iot-app-kit/core';
import { SupportedResolutions } from './util/resolution';
import { MINUTE_IN_MS, HOUR_IN_MS, DAY_IN_MS } from '../common/timeConstants';
import { SiteWiseClientEdge } from './client/edge/client';
const DEFAULT_RESOLUTION_MAPPING = {
    [MINUTE_IN_MS * 15]: SupportedResolutions.ONE_MINUTE,
    [HOUR_IN_MS * 15]: SupportedResolutions.ONE_HOUR,
    [DAY_IN_MS * 60]: SupportedResolutions.ONE_DAY,
};
const isSiteWiseResolution = (resolution) => {
    return Object.values(SupportedResolutions).includes(resolution);
};
export const determineResolution = ({ resolution, start, end, }) => {
    if (typeof resolution === 'string') {
        return resolution;
    }
    const resolutionOverride = resolution || DEFAULT_RESOLUTION_MAPPING;
    const viewportTimeSpan = end.getTime() - start.getTime();
    const matchedViewport = Object.keys(resolutionOverride)
        .sort((a, b) => parseInt(b) - parseInt(a))
        .find((viewport) => viewportTimeSpan >= parseInt(viewport));
    if (matchedViewport) {
        const matchedResolution = resolutionOverride[parseInt(matchedViewport)];
        if (!isSiteWiseResolution(matchedResolution)) {
            throw new Error(`${matchedResolution} is not a valid SiteWise aggregation resolution, must match regex pattern '1m|15m|1h|1d'`);
        }
        return matchedResolution;
    }
    else {
        return '0';
    }
};
export const createDataSource = (siteWise, settings = {}) => {
    const { edgeMode } = settings;
    const client = edgeMode === 'enabled'
        ? new SiteWiseClientEdge(siteWise)
        : new SiteWiseClient(siteWise, settings);
    return {
        initiateRequest: ({ onSuccess, onError }, requestInformations) => {
            Promise.all([
                client.getLatestPropertyDataPoint({
                    onSuccess,
                    onError,
                    requestInformations,
                }),
                client.getAggregatedPropertyDataPoints({
                    requestInformations,
                    onSuccess,
                    onError,
                }),
                client.getHistoricalPropertyDataPoints({
                    requestInformations,
                    onSuccess,
                    onError,
                }),
            ]);
        },
        getRequestsFromQuery: async ({ query, request }) => {
            var _a, _b, _c;
            const resolution = determineResolution({
                resolution: (_a = request.settings) === null || _a === void 0 ? void 0 : _a.resolution,
                start: viewportStartDate(request.viewport),
                end: viewportEndDate(request.viewport),
            });
            const assetRequests = ((_b = query.assets) === null || _b === void 0 ? void 0 : _b.flatMap(({ assetId, properties }) => properties.map(({ propertyId, resolution: resolutionOverride, aggregationType, refId, }) => {
                const finalResolution = resolutionOverride != null ? resolutionOverride : resolution;
                return {
                    id: toId({ assetId, propertyId }),
                    refId,
                    aggregationType: parseDuration(finalResolution) === 0
                        ? undefined
                        : aggregationType,
                    resolution: finalResolution,
                };
            }))) || [];
            const propertyAliasRequests = ((_c = query.properties) === null || _c === void 0 ? void 0 : _c.map(({ propertyAlias, resolution: resolutionOverride, aggregationType, refId, }) => {
                const finalResolution = resolutionOverride != null ? resolutionOverride : resolution;
                return {
                    id: toId({ propertyAlias }),
                    refId,
                    aggregationType: parseDuration(finalResolution) === 0
                        ? undefined
                        : aggregationType,
                    resolution: finalResolution,
                };
            })) || [];
            return [...propertyAliasRequests, ...assetRequests];
        },
    };
};
